import { useEffect, useRef } from 'react';
import { Camera, Clock, Upload, X, AlertTriangle, Loader2, RefreshCcw, FileText } from 'lucide-react';
import { FieldShell } from './FieldShell';
import { useFilePickerState } from './useFilePickerState';
import { useFormPendingUploads } from '../FormPendingUploadsContext';
import type { FieldComponentProps } from './types';

const FILE_ACCEPT = 'application/pdf,image/jpeg,image/jpg,image/png,.pdf,.jpg,.jpeg,.png';

const detectMobile = (): { isIOS: boolean; isAndroid: boolean } => {
  if (typeof navigator === 'undefined') return { isIOS: false, isAndroid: false };
  const ua = navigator.userAgent || '';
  return {
    isIOS: /iPad|iPhone|iPod/i.test(ua),
    isAndroid: /Android/i.test(ua),
  };
};

const isPdf = (entry: { filename?: string; mime_type?: string; resolvedUrl?: string }): boolean => {
  if (entry.mime_type) return entry.mime_type.toLowerCase().includes('pdf');
  const probe = (entry.filename || entry.resolvedUrl || '').toLowerCase();
  return probe.endsWith('.pdf') || probe.includes('application/pdf');
};

export const VersionedPdfPickerField = ({ field, value, error, onChange }: FieldComponentProps) => {
  const c = field.constraints || {};
  // Conservative defaults when the backend omits constraints: single file,
  // max 1. Multi-file requires explicit `allow_multiple: true` in metadata.
  const allowMultiple = c.allow_multiple === true;
  const maxFiles =
    typeof c.max_length === 'number' ? c.max_length : allowMultiple ? 10 : 1;
  const showCameraIOS = (c as any).show_camera_for_ios !== false;
  const showCameraAndroid = (c as any).show_camera_for_android !== false;
  const { isIOS, isAndroid } = detectMobile();
  const cameraVisible = (isIOS && showCameraIOS) || (isAndroid && showCameraAndroid);

  const uploadInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Coerce: the backend may send `document_type_id` as number or numeric string.
  const rawDocTypeId = field.document_type_id;
  const parsedDocTypeId = Number(rawDocTypeId);
  const documentTypeId =
    Number.isFinite(parsedDocTypeId) && Number.isInteger(parsedDocTypeId) && parsedDocTypeId > 0
      ? parsedDocTypeId
      : undefined;
  const { entries, counts, canAddMore, addFiles, remove, retry, uploadPending, uploadDisabled } =
    useFilePickerState({
      value,
      onChange,
      maxFiles,
      allowMultiple,
      documentTypeId,
    });

  // Register `uploadPending` with the form so the submit pass uploads any
  // staged files before validating. Latest closure via ref so re-registers
  // don't churn the registry on every render.
  const pendingRegistry = useFormPendingUploads();
  const uploadPendingRef = useRef(uploadPending);
  uploadPendingRef.current = uploadPending;
  useEffect(() => {
    if (!pendingRegistry) return;
    return pendingRegistry.register(field.title, () => uploadPendingRef.current());
  }, [pendingRegistry, field.title]);

  const handleSelected = (files: FileList | null) => {
    if (!files) return;
    addFiles(files);
  };

  return (
    <FieldShell field={field} error={error}>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs text-rgf-gray-500">
            {counts.total} of {maxFiles} {allowMultiple ? '' : '(single only)'}
            {counts.staged > 0 && ` • ${counts.staged} pending upload`}
            {counts.uploading > 0 && ' • uploading…'}
            {counts.failed > 0 && ` • ${counts.failed} failed`}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm text-rgf-primary-700 hover:text-rgf-primary-800 disabled:opacity-40"
              onClick={() => uploadInputRef.current?.click()}
              disabled={uploadDisabled || !canAddMore}
              title={uploadDisabled ? 'Uploads are disabled in this context.' : undefined}
            >
              <Upload className="h-4 w-4" />
              Upload
            </button>
            {cameraVisible && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-sm text-rgf-primary-700 hover:text-rgf-primary-800 disabled:opacity-40"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploadDisabled || !canAddMore}
              >
                <Camera className="h-4 w-4" />
                Camera
              </button>
            )}
          </div>
        </div>

        <input
          ref={uploadInputRef}
          type="file"
          accept={FILE_ACCEPT}
          multiple={allowMultiple}
          className="hidden"
          onChange={(e) => {
            handleSelected(e.target.files);
            e.target.value = '';
          }}
        />
        {cameraVisible && (
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              handleSelected(e.target.files);
              e.target.value = '';
            }}
          />
        )}

        {uploadDisabled ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            File uploads are not configured for this form.
          </div>
        ) : null}

        {entries.length > 0 && (
          <div className="space-y-2">
            {entries.map((entry) => {
              const pdf = isPdf(entry);
              return (
                <div
                  key={entry.localKey}
                  className={`flex items-center gap-3 rounded-lg border ${entry.error ? 'border-red-300' : 'border-rgf-gray-200'} bg-white px-3 py-2`}
                >
                  <div className="flex-shrink-0 h-12 w-12 rounded-md bg-rgf-gray-50 flex items-center justify-center overflow-hidden">
                    {(() => {
                      // Thumbnail logic: server URL wins, then local blob URL
                      // (for staged or just-uploaded), and finally the right
                      // icon for the entry state.
                      const previewSrc =
                        !entry.uploading && !entry.error
                          ? entry.resolvedUrl ?? entry.localPreviewUrl
                          : undefined;
                      if (entry.uploading) {
                        return <Loader2 className="h-5 w-5 text-rgf-gray-400 animate-spin" />;
                      }
                      if (entry.error) {
                        return <AlertTriangle className="h-5 w-5 text-red-500" />;
                      }
                      // Image thumbnails work for both staged and committed
                      // image entries (PDF picker accepts jpg/png too).
                      if (previewSrc && !pdf) {
                        return (
                          <img
                            src={previewSrc}
                            alt={entry.filename || 'Selected'}
                            className="block h-full w-full object-cover"
                            loading="lazy"
                          />
                        );
                      }
                      if (entry.staged) {
                        return <Clock className="h-5 w-5 text-amber-500" />;
                      }
                      return <FileText className="h-6 w-6 text-rgf-gray-500" />;
                    })()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-rgf-gray-900 truncate" title={entry.filename}>
                      {entry.filename || 'file'}
                    </div>
                    {entry.error ? (
                      <div className="text-xs text-red-600 truncate" title={entry.error}>
                        {entry.pending_submission_id
                          ? `${entry.error} (submission #${entry.pending_submission_id})`
                          : entry.error}
                      </div>
                    ) : entry.uploading ? (
                      <div className="text-xs text-rgf-gray-500">Uploading…</div>
                    ) : entry.staged ? (
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                        <span className="text-amber-700">
                          Pending upload — will upload on submit
                        </span>
                        {entry.localPreviewUrl && (
                          <a
                            href={entry.localPreviewUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-rgf-primary-700 hover:text-rgf-primary-800 hover:underline"
                          >
                            Preview
                          </a>
                        )}
                      </div>
                    ) : entry.resolvedUrl ? (
                      <a
                        href={entry.resolvedUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-rgf-primary-700 hover:text-rgf-primary-800 hover:underline truncate block"
                      >
                        Open
                      </a>
                    ) : (
                      <div className="text-xs text-rgf-gray-500">Preparing preview…</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {entry.error && (
                      <button
                        type="button"
                        aria-label="Retry upload"
                        className="p-1 rounded-full text-rgf-gray-500 hover:text-rgf-gray-900"
                        onClick={() => retry(entry.localKey)}
                      >
                        <RefreshCcw className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      aria-label="Remove file"
                      className="p-1 rounded-full text-rgf-gray-500 hover:text-red-600"
                      onClick={() => remove(entry.localKey)}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </FieldShell>
  );
};
